import MobileJobActions from './MobileJobActions';
import useMobileSchedule from '../../../hooks/useMobileSchedule';
import React, { useState, useEffect, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { FiMove, FiPause, FiX, FiRefreshCw, FiHash, FiUser } from 'react-icons/fi';
import { MapIcon } from 'lucide-react';
import { getOnHoldJobs } from '../../../lib/supabase/schedule';

// ─── Mini draggable card shared between both page types ───────────────────────

const OnHoldCard = ({ assignment, page, quoteType, onDragStart, onMobileMove }) => {
    const isMobile = useMobileSchedule();
    const draggableId = page === 'delivery'
        ? `${assignment.id}-${assignment.quote_info?.quote_type}-${assignment.quote_info?.delivery_count ?? 0}-date`
        : assignment.id;

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: draggableId,
        disabled: isMobile,
        data: { assignment, handleType: page === 'delivery' ? 'date' : undefined },
    });

    // Close the panel the moment the user starts dragging
    useEffect(() => {
        if (isDragging) onDragStart();
    }, [isDragging, onDragStart]);

    const assignedDesigner = assignment?.job_designers?.find(d => d.quote_type === quoteType) || null;

    return (
        <div
            ref={setNodeRef}
            {...(isMobile ? {} : attributes)}
            {...(isMobile ? {} : listeners)}
            className={`bg-white border-2 border-orange-200 rounded-lg p-3 shadow-sm cursor-grab active:cursor-grabbing transition-all select-none ${isDragging ? 'opacity-40 scale-95' : 'hover:shadow-md hover:border-orange-300'}`}
        >
            {/* Job ID row */}
            <div className="flex items-center gap-2 min-w-0 mb-1.5">
                <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="font-semibold text-xs text-white bg-gray-500 rounded px-1 truncate">
                    {assignment.unique_id}
                </span>
                {(assignment.urgent_design || assignment.urgent_quote) && (
                    <span className="bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">
                        URGENT
                    </span>
                )}
            </div>

            {/* Customer */}
            <div className="flex items-center gap-1.5 mb-1">
                <FiUser className="w-3 h-3 text-blue-400 flex-shrink-0" />
                <span className="text-xs text-gray-700 truncate font-medium">
                    {assignment.customer_name}
                </span>
            </div>

            {/* Designer (department page only) */}
            {page === 'department' && (
                <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[10px] text-gray-500">
                        {assignedDesigner ? assignedDesigner.name : 'Not assigned'}
                    </span>
                </div>
            )}

            {/* Address */}
            {assignment.customer_address && (
                <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment.customer_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="flex items-center gap-1 text-[10px] text-green-700 hover:text-green-800 truncate"
                >
                    <MapIcon className="w-2.5 h-2.5 flex-shrink-0" />
                    {assignment.customer_address}
                </a>
            )}

            {isMobile && onMobileMove && <MobileJobActions job={assignment} sheetLayer={10000}><button type="button" aria-label="Move job" title="Move job" className="mt-2 p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-all shadow-md" onClick={e => { e.stopPropagation(); onMobileMove(assignment, e); }}><FiMove className="w-3.5 h-3.5" /></button></MobileJobActions>}
            {/* On Hold badge */}
            <div className="mt-2 pt-1.5 border-t border-orange-100">
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                    <FiPause className="w-2.5 h-2.5" />
                    On Hold
                </span>
            </div>
        </div>
    );
};

// ─── Main panel component ─────────────────────────────────────────────────────

/**
 * OnHoldPanel
 *
 * Props:
 *   page        {string}   'department' | 'delivery'
 *   quoteType   {string}   e.g. 'roof' | 'wall' | 'delivery'  (used for department)
 *   companyId   {string}
 *   userId      {string|null}   current user id (for showOnlyMine support)
 *   showOnlyMine {boolean}
 *
 * Must be rendered INSIDE the parent's <DndContext> so cards are draggable.
 */
const OnHoldPanel = ({ page, quoteType, companyId, userId, showOnlyMine, onMobileMove }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchJobs = useCallback(async () => {
        if (!companyId) return;
        try {
            setLoading(true);
            setError(null);
            const filters = {
                userId: showOnlyMine ? userId : null,
                showOnlyMine: showOnlyMine ? userId : null,
            };
            const { data, error: fetchError } = await getOnHoldJobs(companyId, quoteType, filters);
            if (fetchError) throw fetchError;
            setJobs(data || []);
        } catch (err) {
            setError('Failed to load on-hold jobs.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [companyId, quoteType, showOnlyMine, userId]);

    // Fetch when panel opens
    useEffect(() => {
        if (isOpen) fetchJobs();
    }, [isOpen, fetchJobs]);

    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);

    // Called by a card when the user starts dragging — close the panel immediately
    const handleCardDragStart = useCallback(() => {
        setIsOpen(false);
    }, []);

    return (
        <>
            {/* ── Fixed trigger button ── */}
            <button
                onClick={handleOpen}
                title="View on-hold jobs"
                className={`z-[9990] flex items-center gap-2 px-3 py-1 rounded-lg shadow-lg border font-medium text-sm transition-all
                    ${isOpen
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white text-orange-600 border-orange-300 hover:bg-orange-50'
                    }`} //${page === "delivery" && "fixed top-0 right-8"} 
            >
                <FiPause className="w-4 h-4" />
                <span>On Hold</span>
                {jobs.length > 0 && !isOpen && (
                    <span className="bg-orange-100 text-orange-700 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                        {jobs.length}
                    </span>
                )}
            </button>

            {/* ── Backdrop ── */}
            {isOpen && (
                <div
                    className="fixed inset-0 top-0 !mt-0 z-[9991] bg-black/10"
                    onClick={handleClose}
                />
            )}

            {/* ── Sliding panel ── */}
            <div
                className={`fixed top-0 !mt-0 right-0 h-full w-80 z-[9999] bg-white border-l border-gray-200 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {/* Panel header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-orange-50 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <FiPause className="w-4 h-4 text-orange-600" />
                        <h3 className="font-semibold text-gray-900 text-sm">On Hold</h3>
                        {!loading && (
                            <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">
                                {jobs.length}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={fetchJobs}
                            disabled={loading}
                            title="Refresh"
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                            <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={handleClose}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                        >
                            <FiX className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Helper text */}
                <p className="text-xs text-gray-500 px-4 py-2 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                    Drag a card onto a day to reschedule it.
                </p>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                            <div className="w-8 h-8 border-3 border-orange-400 border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-gray-500">Loading…</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-8">
                            <p className="text-sm text-red-600">{error}</p>
                            <button
                                onClick={fetchJobs}
                                className="mt-2 text-xs text-blue-600 hover:underline"
                            >
                                Try again
                            </button>
                        </div>
                    ) : jobs.length === 0 ? (
                        <div className="text-center py-12">
                            <FiPause className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                            <p className="text-sm text-gray-500 font-medium">No on-hold jobs</p>
                            <p className="text-xs text-gray-400 mt-1">All clear!</p>
                        </div>
                    ) : (
                        jobs.map(assignment => (
                            <OnHoldCard
                                key={`${assignment.id}-${assignment.quote_info?.quote_type ?? 'unknown'}`}
                                assignment={assignment}
                                page={page}
                                quoteType={quoteType}
                                onDragStart={handleCardDragStart}
                                onMobileMove={onMobileMove ? (assignment, event) => { setIsOpen(false); onMobileMove(assignment, event); } : undefined}
                            />
                        ))
                    )}
                </div>
            </div>
        </>
    );
};

export default OnHoldPanel;
